import { ReactNode } from 'react';
import { AddFileToProjectButton } from 'app/_model/project/components/AddFileButton';
import FileGroupTools from 'shared/_model/project-files/FileGroupTools';
import ProjectFile from 'shared/_model/project-files/project-file';
import {
	CountBadge,
	FilesList,
	SubsectionActions,
	SubsectionContainer,
	SubsectionHeader,
	SubsectionLabel,
	ToolDivider,
} from 'shared/_model/project-files/project-files-group/styles';
import { useProjectContext } from 'shared/_pages/project/provider';
import { IconFileMedia, IconFileMisc, IconFileProject, IconMic } from 'shared/_ui/icon';
import { DocType, DocTypeFiles, TDocFile, TDocumentTypeFile } from 'shared/constants/docs';
import { isFinishFile } from 'shared/constants/pouchDB';

const ICON_MAP: Record<TDocumentTypeFile, typeof IconFileMedia | null> = {
	'file-media': IconFileMedia,
	'file-audio': IconMic,
	'file-misc': IconFileMisc,
	'file-project': IconFileProject,
	'file-final-audio': null,
	'file-video': null,
};
export const TITLE_MAP: Record<DocTypeFiles, ReactNode> = {
	[DocType.FILE_MEDIA]: 'Media',
	[DocType.FILE_AUDIO]: 'Audio',
	[DocType.FILE_MISC]: 'Other',
	[DocType.FILE_PROJECT]: 'Project files',
	[DocType.FILE_FINISH_AUDIO]: 'Finished Audio',
	[DocType.FILE_FINISH_VIDEO]: 'Finished Video',
};
const ADD_LABEL_MAP: Record<DocTypeFiles, string> = {
	[DocType.FILE_MEDIA]: 'Add Media',
	[DocType.FILE_AUDIO]: 'Add Audio',
	[DocType.FILE_MISC]: 'Add File',
	[DocType.FILE_PROJECT]: 'Add Project File',
	[DocType.FILE_FINISH_AUDIO]: 'Add Audio',
	[DocType.FILE_FINISH_VIDEO]: 'Add Video',
};
/** Text of the add button for a file type ("Add Media", "Add Video" …). */
export const getAddLabel = (type: DocTypeFiles) => ADD_LABEL_MAP[type];

type ProjectFilesGroupProps = {
	files: TDocFile[];
	type: DocTypeFiles;
};

/**
 * One file type of the project. Media types render as a subsection (icon, label, count, add button and
 * menu, then the file list); finished types render only the list (their header is the page section).
 */
export default function ProjectFilesGroup({ files, type }: ProjectFilesGroupProps) {
	const isFinish = isFinishFile(type);
	const { addFilesNew } = useProjectContext();
	const Icon = ICON_MAP[type];

	const list = (
		<FilesList>
			{files.map((f) => (
				<ProjectFile key={f._id} file={f} />
			))}
		</FilesList>
	);

	if (isFinish) {
		return list;
	}

	return (
		<SubsectionContainer>
			<SubsectionHeader>
				<SubsectionLabel>
					{!!Icon && <Icon />}
					{TITLE_MAP[type]}
					<CountBadge>{files.length}</CountBadge>
				</SubsectionLabel>
				<SubsectionActions data-box-id="file_type_actions">
					{!!addFilesNew && (
						<AddFileToProjectButton type={type} variation="group" label={getAddLabel(type)} />
					)}
					{!!addFilesNew && <ToolDivider />}
					<FileGroupTools type={type} />
				</SubsectionActions>
			</SubsectionHeader>
			{list}
		</SubsectionContainer>
	);
}
